import {defineStore} from 'pinia';
import {computed, ref, watch} from 'vue';
import api from '@/core/api';
import HttpResponse from '@/core/http-module/http-response';
import type {APIapplicationCheckout} from '@/core/api/payment';
import {CountryCode} from '@/config/country/type';
import ApplicationCheckout from '@/core/model/application-checkout';
import Checkout, {type CheckoutLike} from '@/core/model/checkout';
import {useApplicationStore} from '@/store/application';
import useCountry from '@/composables/countryDependable';
import {useProductStore} from '@/store/good';
import type Good from '@/core/model/good';
import LocalStorageDecorator from './storage/local-storage-decorator';

const getCartPrice = async (goods: Good[], countryCode: string, applicationRef: string) => {
    const requestData = {
        applicationRef: applicationRef,
        mark: {
            markGoods: goods,
        }
    };

    const response = await api.application.applicationAmount(requestData, countryCode.toLowerCase());

    if (!(response instanceof HttpResponse)) {
        return null;
    }

    const amountData = response.content.data as APIapplicationCheckout;

    if (countryCode.toUpperCase() !== CountryCode.UK && amountData.classes.length <= 0) {
        throw new Error(`An error occur, get no application-amount from ${countryCode}/application-amount`);
    }

    const application = new ApplicationCheckout(
        amountData.classes,
        amountData.flatFee,
        amountData.extraClassFees,
        applicationRef,
        amountData.subtotal,
        amountData.tax,
        amountData.total,
    );

    const checkout = new Checkout();
    checkout.init(application, amountData.extraClassFees, applicationRef, amountData.total.toString());

    return checkout.getCheckoutLike();
}

const haveSameValues =  (arr1: any[], arr2: any[]) => {
    return JSON.stringify(arr1.sort()) === JSON.stringify(arr2.sort());
}

export const useCartStore = defineStore('cartStore', () => {
    const country = useCountry();
    const applicationStore = useApplicationStore();
    const productStore = useProductStore();
    const cart = ref<null | CheckoutLike>(null);
    
    function resetData() {
        cart.value = null;
    }

    watch(() => productStore.goods, () => {
        updatePrice();
    }, { deep: true });

    const updatePrice = async () => {
        //no goods no price
        if (productStore.classes.length <= 0) {
            return;
        }

        if (cart.value && cart.value.application) {
            const checkoutClassNumbers = cart.value.application.classes.map(item => item.classNumber);
            //No price change if total number of class did not change
            if (haveSameValues(checkoutClassNumbers, productStore.classes)) {
                return;
            }
        }

        const result = await getCartPrice(productStore.goods, country.displayCode, applicationStore.appReference);

        if (result === null) {
            return;
        }

        cart.value = result;
    }

    const totalPrice = computed(() => {
        if (cart.value === null || cart.value.total === null) {
            return 0;
        }
        return cart.value.total;
    })

    updatePrice();
    
    return {cart, totalPrice, resetData};
}, {
    persist: {
        storage: LocalStorageDecorator
    },
});
